import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { FlightDto, MeasureId, ReroutingDto, ScenarioAttributesDto } from 'app/model/CORE';
import KeyUtils from 'app/common/util/KeyUtils';
import { ReroutedFlightDto, ReroutingImpact } from 'app/model/SOL49';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { MiniReroutingEditorPanel } from '../measure/MiniReroutingEditorPanel';
import ReroutedFlightTable, {
    REROUTED_FLIGHT_SELECTED_COLUMN,
    ReroutedFlightColumn,
    StandardReroutingFlightColumn,
} from 'app/components/table/ReroutedFlightTable';
import { Button, notification, Space } from 'antd';
import { ModalHandler } from '../ModalHandler';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection } from 'app/common/table/ManagedTable';
import Utils from 'app/common/util/Utils';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { SCENARIO_ATTRIBUTES_COLUMNS, ScenarionAtributesTable } from '../../table/ScenarioAttributesTable';
import AppContext from 'app/common/context/AppContext';

export const SIMPLE_REROUTED_FLIGHT_COLUMNS: ReroutedFlightColumn[] = [
    REROUTED_FLIGHT_SELECTED_COLUMN,
    StandardReroutingFlightColumn.APPLIED,
    StandardReroutingFlightColumn.ARCID,
    StandardReroutingFlightColumn.ATYPE,
    StandardReroutingFlightColumn.ADEP,
    StandardReroutingFlightColumn.ADES,
    StandardReroutingFlightColumn.IOBT,
    StandardReroutingFlightColumn.MPR,
    StandardReroutingFlightColumn.DELAY,
    StandardReroutingFlightColumn.DELTA_EET,
    StandardReroutingFlightColumn.DELTA_ROUTE_LEN,
    StandardReroutingFlightColumn.DELTA_FUEL_CONSUMPTION,
    StandardReroutingFlightColumn.DELTA_ROUTE_CHARGES,
    StandardReroutingFlightColumn.REASON,
];

interface ReroutingImpactPanelProps {
    rerouting: ReroutingDto;
    reroutingImpact: ReroutingImpact;
    onReroutingEdited?: (measureId: MeasureId, rerouting: ReroutingDto) => void;
    readonly?: boolean;
    onShowFlightOnMap?: (flights: ReroutedFlightDto[]) => void;
}

interface ReroutingImpactPanelState {
    selection: Array<ReroutedFlightDto>;
    scenarioData: ScenarioAttributesDto[];
}

export class ReroutingImpactPanel extends PureComponent<ReroutingImpactPanelProps, ReroutingImpactPanelState> {
    state = {
        selection: [],
        scenarioData: [],
        selectionOnMap: [],
    };

    componentDidMount(): void {
        this.fetchScenario();
    }
    selectAllReroutedFlights = () => {
        // Only select flights that have been rerouted
        const filteredSelection = this.props?.reroutingImpact?.reroutedFlights?.filter(
            flight => flight.reroutingApplied,
        );
        this.setState({ selection: filteredSelection }, () => this.props.onShowFlightOnMap(this.state.selection));
    };

    handleSelectAll = () => {
        this.selectAllReroutedFlights();
    };

    fetchScenario = () => {
        ServiceFactory.stamCoreService()
            .getScenarioList({
                dataset: AppContext.getDataset(),
                fromScenarioIds: [this.props.rerouting.scenarioReference?.scenarioId],
            })
            .then(reply =>
                this.setState({
                    scenarioData: reply.data,
                }),
            )
            .catch(error => notification.error({ message: 'Error', description: error }));
    };

    handleSelectionChange = (selection: Array<ReroutedFlightDto>) => {
        // Allow selection only for valid reroutings
        const newSelection = selection?.filter(reroutedFlight => reroutedFlight.reroutingApplied);

        this.setState({ selection: newSelection }, () => this.props.onShowFlightOnMap(this.state.selection));
        // this.setState({selection: newSelection})
    };

    selection = memoizeOne(
        (selectedReroutedFlights: ReroutedFlightDto[]): MultipleSelection<ReroutedFlightDto> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.handleSelectionChange,
            selectedItems: selectedReroutedFlights,
        }),
    );

    handleReroutingUpdated = (rerouting: ReroutingDto) => {
        const measureId = this.props.rerouting?.measureId;
        this.props.onReroutingEdited?.(measureId, rerouting);
    };

    render() {
        const { rerouting, reroutingImpact } = this.props;
        if (!rerouting) return null;

        const reroutedFlights = reroutingImpact?.reroutedFlights;

        return (
            <Flex direction="vertical">
                <Flex grow={false} style={{ padding: '1em' }}>
                    <MiniReroutingEditorPanel
                        rerouting={rerouting}
                        onReroutingUpdated={this.handleReroutingUpdated}
                        readonly={this.props.readonly}
                    />
                </Flex>
                <Flex direction="vertical" style={{ padding: '1em' }} grow={false}>
                    <Flex style={{ fontSize: '1.5em', lineHeight: '1.5em', fontWeight: 500 }}>SCENARIO INFO</Flex>
                    <Flex style={{ minHeight: '20em' }}>
                        <ScenarionAtributesTable
                            columns={SCENARIO_ATTRIBUTES_COLUMNS}
                            data={this.state.scenarioData}
                            estimatedRowHeight={80}
                        />
                    </Flex>
                </Flex>
                <Flex direction="vertical" style={{ padding: '1em' }} grow={false}>
                    <Flex grow={false} style={{ paddingTop: '1em' }}>
                        <Flex style={{ fontSize: '1.5em', lineHeight: '1.5em', fontWeight: 500 }}>
                            RE-ROUTED FLIGHTS
                        </Flex>
                        <Flex grow={false}>
                            <Space size={[8, 0]}>
                                <Button
                                    disabled={Utils.isEmpty(this.props?.reroutingImpact?.reroutedFlights)}
                                    onClick={this.handleSelectAll}>
                                    Select ALL
                                </Button>
                            </Space>
                        </Flex>
                    </Flex>
                    <Flex className={'solver-rerouted-flight-list'}>
                        <ReroutedFlightTable
                            data={reroutedFlights}
                            columns={SIMPLE_REROUTED_FLIGHT_COLUMNS}
                            keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                            columnResizeDisabled={true}
                            noDataMessage={'No flights'}
                            defaultSorting={[
                                { columnName: StandardReroutingFlightColumn.IOBT.header, direction: 'asc' },
                            ]}
                            selection={this.selection(this.state?.selection)}
                        />
                    </Flex>
                </Flex>
            </Flex>
        );
    }
}
